import { DateTime } from 'luxon';
import {
  GenderEnum,
  IFilter,
  IGlucoseTimeSeries,
  IBloodPressureTimeSeries,
  InformationTooltipEnum,
  IPaginationMetadata,
  RPMCohortViewColorsEnum,
  IGlucoseWidgetTimeSeries,
  ITaskSchedule,
} from './generic.types';
import {
  IBloodPressureTimeSeriesData,
  IRpmPatientInformationAdherenceTrend,
  IReviewedStatus,
  IRpmBloodPressureStruct,
} from './rpm-patient-information.types';
import { TwilioChatTabsEnum } from './twilio-chat-types';
import { ListColumnsEnum } from './views-types';

export type PatientReportDeliveryType = 'allie' | 'documo' | 'ehr';

export interface IBillingCodeEntry {
  code: string;
  count: number;
  description: string;
}

export interface IPatientBillingCodesReportData {
  ccm: Record<string, IBillingCodeEntry>;
  rpm: Record<string, IBillingCodeEntry>;
}

export enum RpmFetchPatientsSearchFieldsEnum {
  NAME = 'name',
  TAGS = 'tags',
}

export enum RpmFetchPatientsSortFieldEnum {
  LASTNAME = 'lastName',
  FIRSTNAME = 'firstName',
  ENROLLMENT = 'dateCreated',
  BIRTHDAY = 'birthday',
  DOB = 'DOB',
  ADHERENCE = 'adherenceAggregate',
  ENGAGEMENT = 'engagementLatest',
  GLUCOSE_TIME_IN_RANGE = 'glucoseTimeInRange',
  LATEST_GLUCOSE = 'latestBloodGlucose',
  LATEST_INSULIN = 'latestInsulin',
  LATEST_WEIGHT = 'latestWeight',
  LATEST_BLOOD_PRESSURE = 'latestBloodPressure',
  LATEST_RESTING_HEART_RATE = 'latestRestingHeartRate',
  LATEST_IRREGULAR_HEART_RHYTHM = 'latestIrregularHeartRhythm',
  RECEIVED = 'messagesLatest',
  MESSAGES = 'messagesUnread',
  TRIAGE_ALERT = 'triageLabel',
  TRIAGE_REASON = 'triageReason',
  TRIAGE_TRIGGERED = 'triageTriggered',
}

export enum TriagePriorityEnum {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum TriagePriorityEnumCapitalized {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
  NONE = 'None',
}

export const TriagePriorityMap: Record<TriagePriorityEnum, string> = {
  [TriagePriorityEnum.LOW]: '#FFCB29',
  [TriagePriorityEnum.MEDIUM]: '#F46700',
  [TriagePriorityEnum.HIGH]: '#EE2A2A',
};

export interface ITriageStatus {
  details: string;
  priority: TriagePriorityEnum;
  reason: string;
  triggered: string;
  id?: string;
}

export enum ColorStringEnum {
  DARK_RED = 'DARK_RED',
  GRAY = 'GRAY',
  PURPLE = 'PURPLE',
  ORANGE = 'ORANGE',
  RED = 'RED',
  YELLOW = 'YELLOW',
}

export interface ITemperatureMeasurement {
  color: ColorStringEnum;
  timestamp: string;
  value: number;
}

export interface ITemperatureAverage {
  color: ColorStringEnum;
  value: number;
}

export interface ITemperatureThresholds {
  criticalHigh: number;
  criticalLow: number;
  high: number;
  low: number;
}

export interface ITemperatureData {
  average: ITemperatureAverage;
  measurements: ITemperatureMeasurement[];
  thresholds: ITemperatureThresholds;
}
export interface IFeedbackAlert {
  feedback: string;
  priority: TriagePriorityEnum;
}

export enum RpmAdminSettingsPatientDeregisteredMethodEnum {
  EHR = 'ehr',
  PATIENT = 'patient',
  PROVIDER = 'provider',
}

export enum RpmAdminSettingsPatientEnrolledMethodEnum {
  EHR = 'ehr',
  MANUAL = 'manual',
}

export enum PatientBulkMessageStatus {
  LOADING = 'loading',
  SENT = 'sent',
  ERROR = 'error',
}

export interface IFilterSchemaToSend {
  filterToSend: string;
  filterTranslation: string;
}

export interface IRpmFetchPatientsPayload {
  page: number;
  limit: number;
  query?: string;
  search?: RpmFetchPatientsSearchFieldsEnum;
  sort?: string;
  filter?: string[];
  fields?: string;
  filtersChanged?: boolean;
}

export interface IRpmFetchPatientsActionPayload extends Omit<
  IRpmFetchPatientsPayload,
  'filter'
> {
  filterString?: string;
  fields?: string;
  cohortId: string;
  listView?: string;
  cohortView?: string;
  mixpanelFilters?: string[];
  customFilterSelected?: boolean;
  selector?: string;
  signal?: AbortSignal;
}

export interface ITimelinePayload {
  patientId: string;
  page: number;
  limit: number;
}

export interface IPaginationPayload {
  page: number;
  pageSize: number;
}

export interface IPatientSignUpIdResponse {
  uuid: string;
  link: string; // url
}

export interface IRpmPatientsListHeader {
  displayName: string | InformationTooltipEnum;
  sortingFieldKey: RpmFetchPatientsSortFieldEnum | string;
  columnEnum?: ListColumnsEnum | string;
}

export interface IRpmPatientSearchField {
  displayName: string;
  fieldKey: RpmFetchPatientsSearchFieldsEnum;
}

export interface IRpmPatientSortField {
  displayName: string;
  fieldKey: RpmFetchPatientsSortFieldEnum;
}

export interface IRpmPatientSearch {
  fields: IRpmPatientSearchField[];
  filters: IFilter[];
  sort: IRpmPatientSortField[];
}

export interface IFiltersPerCohort {
  [key: string]: IFilter[];
}

export interface ISortingPerCohort {
  [key: string]: IRpmPatientSortField[];
}

export interface IFiltersAndSortingsType {
  filters: IFiltersPerCohort;
  sort: ISortingPerCohort;
}

export interface IRPMPatientGenericType {
  patients: IRPMPatient[];
  metadata: IPaginationMetadata;
  search: IRpmPatientSearch;
  totalPatients: number;
  currentPatients: number;
}

export interface IRPMSinglePatient {
  patient: IRPMPatient;
}

export interface IRPMAlertAcknowledgement {
  silenced: boolean;
  timestamp?: string;
  userId?: string;
  userName?: string;
  value?: string;
}

export enum AlertFirstLevelAttributeEnum {
  ADHERENCE = 'adherence',
  BLOOD_GLUCOSE = 'bloodGlucose',
  BLOOD_PRESSURE = 'bloodPressure',
  CGM = 'cgm',
  GLUCOSE = 'glucose',
  GLUCOSE_7_DAYS = 'glucose7days',
  RHR = 'restingHeartRate',
  WEIGHT_CHANGE_PERCENT = 'weightChangeDay',
  WEIGHT_CHANGE_WEEK = 'weightChangeWeek',
  WEIGHT_CHANGE_POUNDS = 'weightChangePounds',
}

export interface IRPMPatientAcknowledgmentPayload {
  patientId: string;
  acknowledgment: IRPMAlertAcknowledgement;
  firstLevelAttribute: AlertFirstLevelAttributeEnum;
  secondLevelAttribute:
    | Exclude<keyof IRPMPatientBloodGlucose, 'units'>
    | undefined
    | 'systolic'
    | 'diastolic';
}

export interface IRPMAlert {
  acknowledgement: IRPMAlertAcknowledgement;
  enabled: boolean;
  carePlanRemoteID: string;
  bloodGlucoseValue?: number;
  remoteID: string;
  taskRemoteID: string;
  outcomeRemoteID?: string;
  value: RPMCohortViewColorsEnum;
}

export interface IRPMBloodGlucoseMeasurement {
  alert: IRPMAlert;
  timestamp?: string;
  color: RPMCohortViewColorsEnum;
  value: number;
  cgm: boolean;
}

export interface IRPPBloodGlucoseAverage {
  value: number;
}

export interface IRPMPatientBloodGlucose {
  average: IRPPBloodGlucoseAverage;
  max: IRPMBloodGlucoseMeasurement;
  min: IRPMBloodGlucoseMeasurement;
  units: string;
}

export interface IRPMPatientMedications {
  [key: string]: IRPMPatientMedicationMeasurement;
}

export interface IRPMPatientMedicationMeasurement {
  percentTaken: number;
  values: IRPMPatientMedicationValuesPerDay;
  color: string | RPMCohortViewColorsEnum;
  name?: string;
}

export interface IRPMPatientMedicationValuesPerDay {
  [key: string]: IRPMPatientMedicationValue;
}

export interface IRPMPatientMedicationValue {
  prescribed: number;
  taken: number;
  date?: string;
}

export interface IAdherenceStruct {
  value: number;
  color: RPMCohortViewColorsEnum;
  alert: IRPMAlert;
}

export interface IRPMAdherence {
  aggregate: IAdherenceStruct;
  activity: IAdherenceStruct;
  bloodPressure: IAdherenceStruct;
  glucose: IAdherenceStruct;
  insulin: IAdherenceStruct;
  medication: IAdherenceStruct;
  weight: IAdherenceStruct;
}

export interface IRPMEngagementMeasurement {
  color: RPMCohortViewColorsEnum;
  timestamp: string | undefined | null;
}

export interface IRPMEngagement {
  weight: IRPMEngagementMeasurement;
  activity: IRPMEngagementMeasurement;
  bloodGlucose: IRPMEngagementMeasurement;
  bloodPressure: IRPMEngagementMeasurement;
  heartRate: IRPMEngagementMeasurement;
  restingHeartRate: IRPMEngagementMeasurement;
  insulin: IRPMEngagementMeasurement;
  latest: IRPMEngagementMeasurement;
}

export interface IHeartRestingStruct {
  color: RPMCohortViewColorsEnum;
  value: number;
  alert: IRPMAlert;
  timestamp?: string;
}

export interface IRPMPatientBloodPressure {
  diastolic: IRpmBloodPressureStruct;
  systolic: IRpmBloodPressureStruct;
  timestamp: string;
}

export interface IBluetoothMeasurements {
  color?: RPMCohortViewColorsEnum;
  value: number;
}

export interface IWeightChangePoundsRangeAlertAckwowledgement {
  silenced: boolean;
  timestamp: string;
  userId: string;
  userName: string;
  value: string;
}

export interface IWeightChangePoundsRangeAlert {
  acknowledgement: IWeightChangePoundsRangeAlertAckwowledgement;
  enabled: boolean;
  carePlanRemoteId: string;
  outcomeRemoteId: string;
  remoteId: string;
  taskRemoteId: string;
  value: RPMCohortViewColorsEnum;
}

export interface IWeightChangePoundsRangeTooltip {
  max: number;
  min: number;
  readings: number;
  valuePercent: number;
  valuePounds: number;
  maxTimestamp: string;
  minTimestamp: string;
}

export interface IWeightChangePoundsRange {
  alert: IRPMAlert;
  max: number;
  min: number;
  readings: number;
  valuePercent: number;
  valuePounds: number;
  maxTimestamp: string;
  minTimestamp: string;
}

export interface IWeightChangePounds {
  oneDay: IWeightChangePoundsRange;
  twoDay: IWeightChangePoundsRange;
  threeDay: IWeightChangePoundsRange;
  oneWeek: IWeightChangePoundsRange;
  twoWeek: IWeightChangePoundsRange;
  fourWeek: IWeightChangePoundsRange;
}

export interface IWeightChange {
  dayChange: IWeightChangeMeasurements;
  twoDayChange: IWeightChangeMeasurements;
  threeDayChange: IWeightChangeMeasurements;
  weekChange: IWeightChangeMeasurements;
  twoWeekChange: IWeightChangeMeasurements;
  fourWeekChange: IWeightChangeMeasurements;
}

export interface IWeightChangeMeasurements {
  alert: IRPMAlert;
  value: number;
  timestamp: string;
}

export interface IRPMPatientEnrollrement {
  timestamp: string;
}

export interface ITemperatureColumnData {
  timestamp: string;
  value: number;
}

export interface ITwilioOpenChatAttributes {
  userId: string;
  contentType: string | TwilioChatTabsEnum;
  unreadMessages: number;
  unreadRecommendations?: number;
  unreadTimelineEvents?: boolean;
  totalNotes: number;
  notes: INotesWidgetNote[];
  cohortId: string;
  patientIdHasChanged: boolean;
  isDetailView?: boolean;
  selectedPatient?: IRPMPatient;
}

export interface ITwilioAttributes {
  userId: string;
  unreadMessages: number;
  twilioUpdated?: boolean;
}

export interface IRPMPatientsPerSelector {
  [key: string]: IRPMPatientsOfSelector;
}

export interface IRPMPatientsOfSelector {
  patients: IRPMPatient[];
  selectorId: string;
  page: number;
  limit: number;
  patientSearch: IRpmPatientSearch;
  filtersChanged?: boolean;
  totalPatients: number;
}

export interface IRPMRemovePatientsOfSelector {
  patients: IRPMPatient[];
  selectorId: string;
}

export interface IRPMPatient {
  adherence: IRPMAdherence;
  birthday: string;
  bloodGlucose: IRPMPatientBloodGlucose;
  bloodGlucoseBluetoothEnabledDays: IBluetoothMeasurements;
  bloodPressure: IRPMPatientBloodPressure;
  bloodPressureBluetoothEnabledDays: IBluetoothMeasurements;
  bmi: IPatientBMI;
  cgm: IRPMPatientBloodGlucose;
  chatbotUnread: boolean;
  clinicians: IRPMPatientAssignedClinician[];
  createdDate: string | null;
  email: string;
  engagement: IRPMEngagement;
  enrollment: IRPMPatientEnrollrement;
  enrollmentStatus: PatientEnrollmentStatusEnum;
  glucose: IRPMPatientBloodGlucose;
  glucose7Days: IRPMPatientBloodGlucose;
  name: IRPMPatientName;
  index?: number;
  sex: GenderEnum | string;
  messages: IPatientMessagesObject;
  recommendations: IPatientRecommendations;
  patientNotes: INotesWidgetNote[];
  notes: INotesWidgetNote[];
  restingHeartRate: IHeartRestingStruct;
  reviewed: IReviewedStatus;
  userId: string;
  weightBluetoothEnabledDays: IBluetoothMeasurements;
  restingheartrateBluetoothEnabledDays: IBluetoothMeasurements;
  weightChange: IWeightChange;
  weightChangePounds: IWeightChangePounds;
  glucoseTimeInRange: IGlucoseTimeInRange;
  phoneNumber: string;
  glucoseTimeInZone: ITimeInZoneChartData;
  bloodPressureTimeInZone: IBloodPressureTimeInZoneChartData;
  latestGlucose: ILatestMeasurement;
  latestInsulin: ILatestMeasurement;
  latestWeight: ILatestMeasurement;
  latestBloodPressure: ILatestMeasurement;
  latestRestingHeartRate: ILatestMeasurement;
  latestIrregularHeartRhythm: ILatestMeasurement;
  twilioUpdated?: boolean;
  lastMessageDate?: Date | string;
  glucoseTimeSeriesToday: IGlucoseTimeSeries;
  glucoseTimeSeriesYesterday: IGlucoseTimeSeries;
  bloodPressureTimeSeriesToday: IBloodPressureTimeSeries;
  bloodPressureTimeSeriesYesterday: IBloodPressureTimeSeries;
  tags: string[];
  temperature?: ITemperatureColumnData;
  weight: IWeightMeasurement;
  triageStatus?: ITriageStatus;
  isChecked?: boolean;
  addNewPatient?: boolean;
}

export interface IRPMPatientAssignedClinician {
  email: string;
  name: string;
  userId: string;
}

export interface IPatientMessagesObject {
  unread: number;
  latest: string;
}

export interface IPatientRecommendations {
  unread: number;
  latest: string;
}

export interface IPatientTwilioAttributes {
  userId: string;
  twilioUpdated: boolean;
  lastMessageDate: Date | string;
  unreadMessages: number;
  timezone?: string;
}

export interface IWeightMeasurement {
  timetamp: string;
  value: number;
}

export interface ILatestMeasurement {
  timestamp: string | null;
  value: number;
}

export interface ILatestBloodPressureMeasurement {
  timestamp: string | null;
  value: ILatestBloodPressureMeasurementValue;
}

export interface ILatestBloodPressureMeasurementValue {
  systolic: number;
  diastolic: number;
}

export interface IGlucoseTimeInRange {
  value: number;
}

export interface ITimeInZoneChartData {
  high: IValueWithAlert;
  low: IValueWithAlert;
  normal: IValueWithAlert;
  veryHigh: IValueWithAlert;
  veryLow: IValueWithAlert;
  total: number;
}

export interface IBloodPressureTimeInZoneChartData {
  hypotension: IValueWithAlert;
  normal: IValueWithAlert;
  elevated?: IValueWithAlert;
  stage1: IValueWithAlert;
  stage2: IValueWithAlert;
  crisis: IValueWithAlert;
  total: number;
}
export type IBloodPressureTimeInZoneWithoutTotal = Omit<
  IBloodPressureTimeInZoneChartData,
  'total'
>;

export interface IValueWithAlert {
  alert: boolean;
  value: number;
}

export interface IStickyNote {
  patientId?: string;
  authorUserId?: string;
  authorName?: string;
  dateCreated?: string;
  createdDate?: string;
  dateDeleted?: string;
  remoteId?: string;
  updatedDate?: string;
  isNew?: boolean;
  text: string;
  color: string;
  index: number;
  title: string;
}

export interface IArchiveNotePayload {
  note: INotesWidgetNote;
  isTwilio: boolean;
}

export interface INotesWidgetResult {
  notes: INotesWidgetNote[];
}

export interface INotesWidgetNote {
  author?: INotesWidgetNoteAuthor;
  dateCreated?: string;
  dateUpdated?: string;
  dateDeleted: string;
  id: string;
  index?: number;
  soap: INotesWidgetNoteSoap;
  sticky: INotesWidgetNoteSticky;
  patientId: string;
  isNew?: boolean;
}

export interface INotesWidgetNoteAuthor {
  name: string;
  userId: string;
}

export interface INotesWidgetNoteSoap {
  assessment: INotesWidgetNoteSoapSection;
  dateRange: INotesWidgetNoteSoapSectionDateRange;
  locked: boolean;
  objective: INotesWidgetNoteSoapSection;
  plan: INotesWidgetNoteSoapSection;
  subjective: INotesWidgetNoteSoapSection;
  appointmentId?: string;
}

export interface INotesWidgetNoteSoapSections {
  assessment: INotesWidgetNoteSoapSection;
  objective: INotesWidgetNoteSoapSection;
  plan: INotesWidgetNoteSoapSection;
  subjective: INotesWidgetNoteSoapSection;
}

export interface INotesWidgetNoteSoapSection {
  text: string;
  visible: boolean;
  autogenerate?: boolean;
  autogenerated?: boolean;
}

export interface INotesWidgetNoteSoapSectionDateRange {
  start: string;
  end: string;
}

export interface INotesWidgetNoteSticky {
  title: string;
  text: string;
  index: number;
  color: string;
}

export interface IStickyNoteEdit {
  isEdit: boolean;
  isNew: boolean;
  noteId: string;
}

export interface IStickyNoteUpdate {
  isUpdated: boolean;
  isNew: boolean;
}

export interface IStickyNoteForMixpanel {
  note: INotesWidgetNote;
  deleted: boolean;
  patientId: string;
}

export enum StickyNoteTypesEnum {
  ALL = 'ALL_NOTES',
  STICKIES = 'STICKIES',
  SOAP = 'SOAP',
  ARCHIVED = 'ARCHIVED',
}

export enum StickyNotesColorEnum {
  LIGHT = 'light',
  MEDIUM = 'medium',
  DARK = 'dark',
}

export interface IPatientNote {
  noteToDelete: INotesWidgetNote;
  patientId?: string;
  updatedNotes: INotesWidgetNote[];
  allNotes: INotesWidgetNote[];
  isTwilio: boolean;
}

export interface IPatientNotes {
  stickyNotes: INotesWidgetNote[];
  allNotes?: INotesWidgetNote[];
  patientId?: string;
  isTwilio?: boolean;
  isRestore?: boolean;
}

export interface IStickyNoteTextWithIndex {
  noteText: string;
  noteIndex: number;
}

export interface IStickyNoteWithIndex {
  note: IStickyNote;
  noteIndex: number;
}

export interface IStickyNotesResult {
  notes: IStickyNote[];
}

export interface IRPMPatientName {
  namePrefix: string;
  givenName: string;
  middleName: string;
  familyName: string;
  nameSuffix: string;
  nickName: string;
  preferred: string;
}

export interface IPatientUniversalLinkInfo {
  cohortId?: string;
  detailViewId?: string;
  error?: string;
}

export enum PatientEnrollmentStatusEnum {
  REGISTERED = 'Registered',
  NOT_REGISTERED = 'Not Registered',
  UNKNOWN = 'Unknown',
}

export interface IRpmEditablePatientFields {
  phone?: string;
  phoneSecondary?: string;
}

export interface IRpmEditablePatientFieldsContactInfo {
  contact?: {
    address?: {
      address1?: string;
      address2?: string;
      city?: string;
      country?: string;
      state?: string;
      zip?: string;
    };
    occupation?: string;
    phone?: string;
  };
  preferences?: {
    hours?: string;
    language?: string;
    method?: string;
    proxyName?: string;
    proxyNumber?: string;
    timezone?: string;
  };
  provider?: {
    name?: string;
    phone?: string;
  };
}

export interface IRpmEditablePatientFieldsTags {
  tags: string[];
}

export interface IRpmEditablePatientFieldsMRN {
  mrn: string;
}

export interface IAdminSettingsPatient {
  deregisteredDate: string | null | undefined;
  name: IRPMPatientName;
  email: string;
  userId: string;
  registeredDate: string | null | undefined;
  enrolledDate: string | null | undefined;
  cohorts?: IAdminSettingsPatientCohort[];
  deregisteredMethod: RpmAdminSettingsPatientDeregisteredMethodEnum | string;
  deregisteredNote: string;
  deregisteredReason: string;
}

export interface IAdminSettingsPatientsResponse {
  lastSync?: string;
  patients: IAdminSettingsPatient[];
}

export interface IAdminSettingsPatientCohort {
  name: string;
  assignedDate: string;
  method: string | RpmAdminSettingsPatientEnrolledMethodEnum;
  remoteId: string;
}

export interface IAdmingSettingsAssignedPatientCohort {
  assigned: string;
  method: string | RpmAdminSettingsPatientEnrolledMethodEnum;
  id: string;
}

export interface IPatientsToAssign {
  patientId: string;
  assign?: boolean;
}

export interface IPatientDeviceWidgetInfo {
  devicesFetched: boolean;
  devices: IPatientDevices;
}

export enum TicketSourceEnum {
  PYLON = 'PYLON',
  ZENDESK = 'ZENDESK',
}

export interface IZendeskTicket {
  dateCreated: string;
  dateUpdated: string;
  source: string;
  status: string;
  ticketNumber: string;
  title: string;
  url: string;
}

export interface IPatientZendeskWidgetInfo {
  ticketsFetched?: boolean;
  tickets: IZendeskTicket[];
}

export enum ZendeskTicketsStatusEnum {
  OPEN_TICKETS = 'Open Tickets',
  CLOSED_TICKETS = 'Closed Tickets',
}

export interface IPatientDevices {
  connectBloodPressure: boolean;
  connectGlucose: boolean;
  connectWeight: boolean;
  mobile: IPatientMobileDevice;
  peripherals: IPatientPeripheralDevices[];
  integrations: IPatientIntergratedDevices[];
}

export interface IPatientMobileDevice {
  appVersion: string;
  model: string;
  engagement: string;
  marketingName: string;
  platform: string;
  platformVersion: string;
}

export interface IPatientPeripheralDevices {
  id: string;
  type: string;
  name: string;
  lastSyncDate: string;
}

export interface IPatientIntergratedDevices {
  name: string;
  lastSyncDate: string;
}

export interface IPatientSurveysWidgetInfo {
  surveysFetched: boolean;
  surveys: IPatientWidgetSurveys;
}

export interface IPatientWidgetSurveys {
  surveys: IPatientWidgetSurvey[];
}

export interface IPatientWidgetSurvey {
  questionAnswers: IPatientWidgetSurveyQuestion[];
  submittedAt: string;
  title: string;
  type: string;
  url: string;
}

export interface IPatientWidgetSurveyQuestion {
  answer: string;
  question: string;
}

export interface IPatientTasksWidgetInfo {
  tasksFetched: boolean;
  tasks: IPatientTasksWidgetTask[];
}

export interface IPatientTasksWidgetTask {
  remoteId: string;
  asset: string;
  dateCompleted: string;
  description: string;
  image: string;
  title: string;
  type: string;
}

export interface IPatientMedicationsWidget {
  medications: IPatientMedicationsWidgetData[];
}

export interface IPatientMedicationsWidgetInfo {
  medicationsFetched: boolean;
  medications: IPatientMedicationsWidgetData[];
}

export interface IPatientMedicationsWidgetData {
  dosage: number;
  doses: IPatientMedicationsWidgetDoses[];
  instructions: string;
  name: string;
  units: string;
}

export interface IPatientMedicationsWidgetDoses {
  ingredient: string;
  strength: number;
  units: string;
}

export interface IPatientBloodPressureWidgetInfo {
  bloodPressureFetched: boolean;
  bloodPressure: IPatientBloodPressureWidgetData;
}

export interface IPatientBloodPressureWidgetData {
  baseline: IPatientBloodPressureWidgetDataBaseline[];
  maxDiastolic: IPatientBloodPressureWidgetDataMaxAndMin;
  maxSystolic: IPatientBloodPressureWidgetDataMaxAndMin;
  minDiastolic: IPatientBloodPressureWidgetDataMaxAndMin;
  minSystolic: IPatientBloodPressureWidgetDataMaxAndMin;
  timeInZone: IBloodPressureTimeInZoneChartData;
  today: IBloodPressureTimeSeriesData[];
  yesterday: IBloodPressureTimeSeriesData[];
  zones: IPatientWidgetMetricZone[];
}

export interface IPatientBloodPressureWidgetDataBaseline {
  count: number;
  diastolic: {
    color: string;
    category: string;
    value: number;
  };
  systolic: {
    color: string;
    category: string;
    value: number;
  };
  timestamp: string;
}

export interface IPatientBloodPressureWidgetDataMaxAndMin {
  color: string;
  value: number;
  category: PatientBloodPressureCategoryEnum | string;
}

export enum PatientBloodPressureCategoryEnum {
  HYPOTENSION = 'HYPOTENSION',
  CONTROLLED = 'CONTROLLED',
  ELEVATED = 'ELEVATED',
  STAGE_1 = 'STAGE_1',
  STAGE_2 = 'STAGE_2',
  CRISIS = 'CRISIS',
}

export interface IPatientBloodGlucoseWidgetInfo {
  bloodGlucoseFetched: boolean;
  bloodGlucose: IPatientBloodGlucoseWidgetData;
}

export interface IPatientBloodGlucoseWidgetData {
  a1cs: IPatientBloodGlucoseWidgetDataA1C[];
  max: IPatientBloodGlucoseWidgetDataMaxAndMin;
  min: IPatientBloodGlucoseWidgetDataMaxAndMin;
  timeInZone: ITimeInZoneChartData;
  today: IGlucoseWidgetTimeSeries;
  yesterday: IGlucoseWidgetTimeSeries;
  zones: IPatientWidgetMetricZone[];
}

export interface IPatientBloodGlucoseWidgetDataA1C {
  color: RPMCohortViewColorsEnum | string;
  dateCreated: string;
  dateUpdated: string;
  firstName: string;
  lastName: string;
  preProgram: boolean;
  remoteId: string;
  source: string;
  userId: string;
  value: number;
}

export interface IPatientBloodGlucoseWidgetA1CEstimate {
  color: string;
  value: number;
}

export interface IPatientBloodGlucoseWidgetDataMaxAndMin {
  color: string;
  value: number;
}

export interface IPatientBMI {
  height: IPatientBMIMeasurement;
  value: number;
  weight: IPatientBMIMeasurement;
}

export interface IPatientBMIMeasurement {
  timestamp: string;
  value: number;
}

export interface IPatientWidgetMetricZone {
  color: RPMCohortViewColorsEnum | string;
  description: string;
  title: string;
}

export interface IPatientAdherenceWidgetInfo {
  adherenceFetched: boolean;
  adherence: IPatientAdherenceWidgetData;
}

export interface IPatientAdherenceWidgetData {
  overall: IPatientAdherenceWidgetOverall;
  measurement?: IPatientAdherenceWidgetMeasurement;
  activity: IPatientAdherenceWidgetMeasurement;
  bloodPressure: IPatientAdherenceWidgetMeasurement;
  glucose: IPatientAdherenceWidgetMeasurement;
  insulin: IPatientAdherenceWidgetMeasurement;
  medication: IPatientAdherenceWidgetMedicationMeasurement;
  weight: IPatientAdherenceWidgetMeasurement;
  zones: IPatientWidgetMetricZone[];
}

export interface IPatientAdherenceWidgetOverall {
  value: number;
  color: string;
  trend?: IRpmPatientInformationAdherenceTrend;
}

export interface IPatientAdherenceWidgetMeasurements {
  activity: IPatientAdherenceWidgetMeasurement;
  bloodPressure: IPatientAdherenceWidgetMeasurement;
  glucose: IPatientAdherenceWidgetMeasurement;
  insulin: IPatientAdherenceWidgetMeasurement;
  medication: IPatientAdherenceWidgetMedicationMeasurement;
  weight: IPatientAdherenceWidgetMeasurement;
}

export interface IPatientAdherenceWidgetMeasurement {
  updated: string;
  value: number;
  color: string;
  schedules?: ITaskSchedule[];
  trend?: IRpmPatientInformationAdherenceTrend;
}

export interface IPatientAdherenceWidgetMedicationMeasurement {
  updated: string;
  value: number;
  color: string;
  medications: IPatientAdherenceWidgetMedications[];
  trend?: IRpmPatientInformationAdherenceTrend;
}

export interface IPatientAdherenceWidgetMedications {
  adherence: number;
  color: string | RPMCohortViewColorsEnum;
  name: string;
  schedules: ITaskSchedule[];
}

export interface IPatientGoalsWidgetData {
  cohort: string[];
  goals: string[];
}

export interface IPatientCommunicationWidgetProvidersResponse {
  providers: string[];
}

export interface IPatientCommunicationWidgetData {
  contact: IPatientCommunicationWidgetContactDetails;
  preferences: IPatientCommunicationWidgetCommunicationPreferences;
  provider: IPatientCommunicationWidgetHealthCareProvider;
}

export interface IPatientCommunicationReportData {
  messagesSent: number;
  messagesReceived: number;
}

export interface IPatientCommunicationWidgetContactDetails {
  phone: string;
  email: string;
  address: IPatientCommunicationWidgetContactDetailsAddress;
  occupation: string;
  location?: string;
}

export interface IPatientCommunicationWidgetContactDetailsAddress {
  address1: string;
  address2: string;
  country: string;
  state: string;
  city: string;
  zip: string;
}

export interface IPatientCommunicationWidgetCommunicationPreferences {
  hours: string;
  method: string;
  proxyName: string;
  proxyNumber: string;
  timezone: string;
  language: string;
}

export interface IPatientCommunicationWidgetHealthCareProvider {
  email: string;
  name: string;
  phone: string;
  serviceOrders: ServiceOrdersEnum[];
}

export interface IPatientCommunicationWidgetField {
  [key: string]: string;
}

export interface IPatientVitalsWidgetInfo {
  vitalsFetched: boolean;
  vitals: IPatientVitalsWidgetData;
}

export interface IPatientVitalsWidgetData {
  restingHeartRate: IPatientVitalsWidgetRHR;
  bmi: IPatientVitalsWidgetBMI;
  height: IPatientVitalsWidgetHeight;
  weight: IPatientVitalsWidgetWeight;
  referenceWeight: IPatientVitalsReferenceWeight;
}

export interface IPatientVitalsWidgetRHR {
  timestamp: string;
  trend: IPatientVitalsWidgetRHRTrend;
  value: number;
}

export interface IPatientVitalsWidgetRHRTrend {
  category: string;
  change: number;
}

export interface IPatientVitalsWidgetBMI {
  category: string;
  color: string;
  value: number;
  timestamp: string;
}

export interface IPatientVitalsWidgetHeight {
  timestamp: string;
  value: number;
}

export interface IPatientVitalsWidgetWeight {
  timestamp: string;
  trend: IPatientVitalsWidgetWeightTrend;
  value: number;
}

export interface IPatientVitalsReferenceWeight {
  note: string;
  noteAuthor: string;
  timestamp: string;
  value: number;
}

export interface IPatientVitalsWidgetWeightTrend {
  category: string;
  change: number;
}

export interface ICreateOrUpdateReferenceWeightPayload {
  note: string;
  timestamp: string;
  value: number;
}

export interface IPatientTimeTrackerWidgetData {
  direct: number;
  indirect: number;
  total: number;
}

export interface IPatientTimeTrackWidgetTrackCall {
  date: DateTime;
  duration: number;
  startTime: string;
  endTime: string;
  isPatientRequested: boolean;
  purpose: string;
}

export interface IClinician {
  email: string;
  firstName: string;
  lastName: string;
  userId: string;
}

export interface IFeedbackClinician {
  clinician: IClinician;
  dislike: boolean;
  like: boolean;
  reason: string;
}

export interface ISource {
  link: string;
  title: string;
}

export interface IChatbotMessage {
  content: string;
  dateCreated: string;
  feedbackClinician: IFeedbackClinician;
  model: string;
  remoteId: string;
  role: string;
  sources: ISource[];
}

export interface IChatbotMessagesResponse {
  lastReadMessageId: string;
  lastReadMessageIdByPatient: string;
  lastReadTimestampByPatient: string;
  messages: IChatbotMessage[];
  unread: boolean;
}

export interface IPatientPaginatedPayload {
  page: number;
  limit: number;
  patientId: string;
}

export interface IChatbotFeedback {
  like: boolean;
  dislike: boolean;
  reason: string;
}

export interface IChatbotFeedbackPayload {
  messageId: string;
  feedback: IChatbotFeedback;
  patientId: string;
}

export interface IChatbotReadIndexPayload {
  lastReadMessageId: string;
  patientId: string;
}

export interface IChatbotReadIndexResponse {
  lastReadMessageId: string;
  lastReadMessageIdByPatient: string;
  lastReadTimestampByPatient: string;
}

export interface IChatbotThreadSummary {
  threadId: string;
  dateCreated: string;
  dateUpdated: string;
  title: string;
  lastMessagePreview: string;
  lastReadMessageId: string;
  lastReadMessageIdByPatient: string;
  lastReadTimestampByPatient: string;
  unread: boolean;
}

export interface IChatbotThreadsResponse {
  threads: IChatbotThreadSummary[];
  unread: boolean;
}

export interface IChatbotThreadMessagesResponse {
  threadId: string;
  lastReadMessageId: string;
  lastReadMessageIdByPatient: string;
  lastReadTimestampByPatient: string;
  messages: IChatbotMessage[];
  unread: boolean;
}

export interface IPatientThreadsPayload {
  patientId: string;
  page: number;
  limit: number;
}

export interface IPatientThreadMessagesPayload {
  patientId: string;
  threadId: string;
  before?: string;
  limit: number;
}

export interface IChatbotThreadReadIndexPayload {
  patientId: string;
  threadId: string;
  lastReadMessageId: string;
}

export interface IChatbotThreadFeedbackPayload {
  patientId: string;
  threadId: string;
  messageId: string;
  feedback: IChatbotFeedback;
}

export enum TasksWidgetTaskTypesEnum {
  GOALS = 'GOALS',
  QUIZ = 'QUIZ',
  SURVEY = 'SURVEY',
  VIDEO = 'VIDEO',
  EDUCATION_CUSTOM = 'EDUCATION_CUSTOM',
  EDUCATION_PDF = 'EDUCATION_PDF',
  EDUCATION_WEB = 'EDUCATION_WEB',
}

export enum ServiceOrdersEnum {
  RPM = 'RPM',
  NON_COMPLEX_CCM = 'Non-Complex CCM',
  COMPLEX_CCM = 'Complex CCM',
}

export enum SourcesForA1CEnum {
  SELECT = 'Select...',
  CLINIC = 'Clinic',
  EHR = 'EHR',
  SELF_REPORT = 'Self-report',
}

export interface IWidgetCheckInAverageResponse {
  units: string;
  value: number;
}

export interface IWidgetCheckInWeekResponse {
  display: string;
  endDate: string;
  startDate: string;
  timezone: string;
  units: string;
  value: number;
}

export interface IWidgetCheckInResponse {
  average: IWidgetCheckInAverageResponse;
  max: number;
  min: number;
  title: string;
  weeks: IWidgetCheckInWeekResponse[];
}

export interface IWidgetCheckInsResponse {
  checkIns: IWidgetCheckInResponse[];
}

export interface ICheckInChartItemResponse {
  average: IWidgetCheckInAverageResponse;
  title: string;
  weeks: IWidgetCheckInWeekResponse[];
}

export interface ICheckInChartResponse {
  checkIns: ICheckInChartItemResponse[];
}
